import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useEffect, useMemo, useRef, useState } from "react";
import { useServerFn } from "@tanstack/react-start";
import { supabase } from "@/integrations/supabase/client";
import { ragAnswer, listMatterConversations, getRagConversation, type Citation } from "@/lib/rag.functions";
import { extractAndIndexDocument } from "@/lib/embeddings.functions";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { ArrowLeft, Send, Loader2, FileText, Sparkles, MessageSquare, Plus, Upload } from "lucide-react";
import { toast } from "sonner";
import ReactMarkdown from "react-markdown";

export const Route = createFileRoute("/_authenticated/app/matters/$matterId")({
  component: MatterDetail,
});

type Matter = { id: string; name: string; number: string | null; status: string | null; description: string | null; client_id: string | null };
type Doc = { id: string; name: string; indexed_at: string | null; storage_path: string | null };
type Msg = { id?: string; role: "user" | "assistant"; content: string; metadata?: any };

function MatterDetail() {
  const { matterId } = Route.useParams();
  const navigate = useNavigate();
  const [matter, setMatter] = useState<Matter | null>(null);
  const [docs, setDocs] = useState<Doc[]>([]);
  const [selectedDocs, setSelectedDocs] = useState<string[]>([]);
  const [conversations, setConversations] = useState<{ id: string; title: string; updated_at: string }[]>([]);
  const [convId, setConvId] = useState<string | null>(null);
  const [messages, setMessages] = useState<Msg[]>([]);
  const [question, setQuestion] = useState("");
  const [sending, setSending] = useState(false);
  const [activeCitation, setActiveCitation] = useState<Citation | null>(null);
  const [indexingId, setIndexingId] = useState<string | null>(null);
  const scrollRef = useRef<HTMLDivElement>(null);

  const ragFn = useServerFn(ragAnswer);
  const listConvs = useServerFn(listMatterConversations);
  const getConv = useServerFn(getRagConversation);
  const indexFn = useServerFn(extractAndIndexDocument);

  const load = async () => {
    const [{ data: m }, { data: d }] = await Promise.all([
      supabase.from("matters").select("*").eq("id", matterId).maybeSingle(),
      supabase.from("library_documents").select("id, name, indexed_at, storage_path").eq("matter_id", matterId).order("created_at", { ascending: false }),
    ]);
    setMatter((m as any) ?? null);
    setDocs((d as any) ?? []);
    const c = await listConvs({ data: { matterId } });
    if (c.ok) setConversations(c.items as any);
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [matterId]);
  useEffect(() => { scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight }); }, [messages.length]);

  const openConv = async (id: string) => {
    setConvId(id);
    const r = await getConv({ data: { conversationId: id } });
    if (r.ok) setMessages(r.messages.map((m: any) => ({ id: m.id, role: m.role, content: m.content, metadata: m.metadata })));
  };

  const newChat = () => {
    setConvId(null);
    setMessages([]);
    setActiveCitation(null);
  };

  const send = async () => {
    const q = question.trim();
    if (!q) return;
    setSending(true);
    setMessages((m) => [...m, { role: "user", content: q }]);
    setQuestion("");
    try {
      const r = await ragFn({
        data: {
          question: q,
          matterId,
          documentIds: selectedDocs.length ? selectedDocs : undefined,
          conversationId: convId,
        },
      });
      if (!r.ok) {
        toast.error(r.error);
        setMessages((m) => [...m, { role: "assistant", content: `⚠️ ${r.error}` }]);
      } else {
        setConvId(r.conversationId);
        setMessages((m) => [...m, { role: "assistant", content: r.content, metadata: { citations: r.citations } }]);
        if (r.citations.length === 0) toast.info("Nessuna fonte trovata nei documenti indicizzati");
      }
    } catch (e: any) {
      toast.error(e?.message ?? "Errore");
    } finally {
      setSending(false);
    }
  };

  const indexDoc = async (id: string) => {
    setIndexingId(id);
    try {
      const r = await indexFn({ data: { documentId: id } });
      if (!r.ok) toast.error(r.error);
      else { toast.success(`Indicizzato (${r.chunks} brani)`); load(); }
    } finally { setIndexingId(null); }
  };

  const indexedCount = useMemo(() => docs.filter((d) => d.indexed_at).length, [docs]);

  return (
    <div className="flex h-[calc(100vh-3.5rem)] min-w-0">
      {/* Left column */}
      <aside className="hidden w-72 shrink-0 flex-col border-r border-[var(--lex-border)] bg-secondary lg:flex">
        <div className="border-b border-[var(--lex-border)] p-4">
          <Button variant="ghost" size="sm" className="mb-2 -ml-2 gap-1" onClick={() => navigate({ to: "/app/matters" })}>
            <ArrowLeft className="h-3.5 w-3.5" /> Pratiche
          </Button>
          <div className="font-display text-lg font-semibold leading-tight">{matter?.name ?? "Pratica"}</div>
          <div className="mt-1 text-xs text-muted-foreground">{matter?.number ?? "-"}</div>
          {matter?.status && <Badge variant="secondary" className="mt-2 text-[10px]">{matter.status}</Badge>}
        </div>

        <div className="flex-1 overflow-y-auto p-3">
          <div className="mb-1 px-1 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
            Documenti collegati ({indexedCount}/{docs.length} indicizzati)
          </div>
          {docs.length === 0 && (
            <div className="rounded-md border border-dashed p-4 text-center text-xs text-muted-foreground">
              <Upload className="mx-auto mb-2 h-5 w-5" />
              Carica documenti dalla <Link to="/app/library" className="underline">Libreria</Link> e collegali a questa pratica.
            </div>
          )}
          <div className="space-y-1">
            {docs.map((d) => {
              const checked = selectedDocs.includes(d.id);
              return (
                <div key={d.id} className="rounded-md border border-[var(--lex-border)] bg-primary p-2 text-xs">
                  <label className="flex items-start gap-2">
                    <input
                      type="checkbox" checked={checked}
                      onChange={(e) => setSelectedDocs((s) => e.target.checked ? [...s, d.id] : s.filter((x) => x !== d.id))}
                      className="mt-0.5"
                    />
                    <div className="min-w-0 flex-1">
                      <div className="truncate font-medium" title={d.name}>{d.name}</div>
                      <div className="mt-1 flex items-center gap-1">
                        {d.indexed_at ? (
                          <Badge variant="secondary" className="h-4 px-1 text-[9px]">indicizzato</Badge>
                        ) : (
                          <Button variant="outline" size="sm" className="h-5 px-1.5 text-[9px]" disabled={indexingId === d.id} onClick={() => indexDoc(d.id)}>
                            {indexingId === d.id ? <Loader2 className="h-2.5 w-2.5 animate-spin" /> : "Indicizza"}
                          </Button>
                        )}
                      </div>
                    </div>
                  </label>
                </div>
              );
            })}
          </div>

          <div className="mt-5 mb-1 flex items-center justify-between px-1">
            <span className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">Conversazioni</span>
            <Button variant="ghost" size="sm" className="h-6 px-1.5" onClick={newChat}><Plus className="h-3 w-3" /></Button>
          </div>
          <div className="space-y-1">
            {conversations.map((c) => (
              <button key={c.id} onClick={() => openConv(c.id)} className={`block w-full truncate rounded-md px-2 py-1.5 text-left text-xs hover:bg-primary ${convId === c.id ? "bg-primary font-medium" : ""}`}>
                {c.title}
              </button>
            ))}
            {conversations.length === 0 && <div className="px-1 text-[11px] text-muted-foreground">Nessuna conversazione.</div>}
          </div>
        </div>
      </aside>

      {/* Center: chat */}
      <section className="flex min-w-0 flex-1 flex-col bg-primary">
        <div className="border-b border-[var(--lex-border)] px-6 py-3">
          <div className="flex items-center gap-2">
            <MessageSquare className="h-4 w-4 text-muted-foreground" />
            <h1 className="font-display text-base font-semibold">Chat documentale</h1>
            <Badge variant="outline" className="ml-2 text-[10px]"><Sparkles className="mr-1 h-2.5 w-2.5" /> RAG · solo fonti caricate</Badge>
          </div>
          <p className="mt-0.5 text-xs text-muted-foreground">Risposte ancorate ai documenti indicizzati di questa pratica, con citazioni.</p>
        </div>

        <div ref={scrollRef} className="flex-1 overflow-y-auto px-6 py-5">
          {messages.length === 0 ? (
            <Card className="mx-auto max-w-xl p-6 text-center">
              <Sparkles className="mx-auto mb-3 h-8 w-8 text-muted-foreground" />
              <h3 className="font-display text-lg font-semibold">Fai una domanda sui documenti</h3>
              <p className="mt-1 text-sm text-muted-foreground">
                {indexedCount === 0
                  ? "Indicizza almeno un documento dalla colonna a sinistra per iniziare."
                  : "Es. \"Quali sono le penali contrattuali?\" oppure \"Riassumi gli obblighi della parte A.\""}
              </p>
            </Card>
          ) : (
            <div className="mx-auto max-w-3xl space-y-4">
              {messages.map((m, i) => (
                <div key={i} className={m.role === "user" ? "flex justify-end" : "flex justify-start"}>
                  <div className={m.role === "user"
                    ? "max-w-[80%] rounded-2xl rounded-tr-sm bg-foreground px-4 py-2.5 text-sm text-background"
                    : "max-w-[85%] rounded-2xl rounded-tl-sm bg-secondary px-4 py-3 text-sm"}>
                    {m.role === "user" ? (
                      <p className="whitespace-pre-wrap">{m.content}</p>
                    ) : (
                      <div className="prose prose-sm max-w-none">
                        <ReactMarkdown>{m.content}</ReactMarkdown>
                        {Array.isArray(m.metadata?.citations) && m.metadata.citations.length > 0 && (
                          <div className="not-prose mt-3 flex flex-wrap gap-1.5 border-t pt-2">
                            {m.metadata.citations.map((c: Citation) => (
                              <button
                                key={c.idx}
                                onClick={() => setActiveCitation(c)}
                                className="inline-flex items-center gap-1 rounded-md border border-[var(--lex-border)] bg-primary px-2 py-0.5 text-[11px] font-medium hover:border-foreground"
                              >
                                <FileText className="h-2.5 w-2.5" />[{c.idx}] {c.document_name}{c.page_number ? ` · pag. ${c.page_number}` : ""}
                              </button>
                            ))}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              ))}
              {sending && (
                <div className="flex justify-start">
                  <div className="rounded-2xl bg-secondary px-4 py-3 text-sm text-muted-foreground">
                    <Loader2 className="inline h-3.5 w-3.5 animate-spin" /> Sto cercando nei documenti…
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        <div className="border-t border-[var(--lex-border)] bg-primary px-6 py-3">
          <div className="mx-auto flex max-w-3xl items-end gap-2">
            <Textarea
              value={question}
              onChange={(e) => setQuestion(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
              placeholder={indexedCount === 0 ? "Indicizza prima un documento…" : "Fai una domanda sui documenti della pratica…"}
              rows={2}
              disabled={sending}
              className="min-h-[44px] resize-none"
            />
            <Button onClick={send} disabled={sending || !question.trim()} className="h-11">
              {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
            </Button>
          </div>
          {selectedDocs.length > 0 && (
            <div className="mx-auto mt-2 max-w-3xl text-[11px] text-muted-foreground">
              Filtro attivo: {selectedDocs.length} documento/i selezionato/i
            </div>
          )}
        </div>
      </section>

      {/* Right: citation panel */}
      {activeCitation && (
        <aside className="hidden w-80 shrink-0 flex-col border-l border-[var(--lex-border)] bg-secondary xl:flex">
          <div className="flex items-center justify-between border-b border-[var(--lex-border)] p-3">
            <span className="text-xs font-semibold">Citazione [{activeCitation.idx}]</span>
            <button onClick={() => setActiveCitation(null)} className="text-xs text-muted-foreground hover:text-foreground">Chiudi</button>
          </div>
          <div className="flex-1 overflow-y-auto p-4">
            <div className="mb-2 font-medium">{activeCitation.document_name}</div>
            {activeCitation.page_number && <div className="mb-3 text-xs text-muted-foreground">Pagina {activeCitation.page_number}{activeCitation.section_title ? ` · ${activeCitation.section_title}` : ""}</div>}
            <div className="rounded-md border border-[var(--lex-border)] bg-primary p-3 text-xs leading-relaxed whitespace-pre-wrap">
              {activeCitation.snippet}
            </div>
            <div className="mt-3 text-[11px] text-muted-foreground">Similarità: {(activeCitation.similarity * 100).toFixed(1)}%</div>
          </div>
        </aside>
      )}
    </div>
  );
}
